import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi, formatApiErrorDetail } from "../../../lib/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Percent, RefreshCw, Zap, Hand } from "lucide-react";

export default function RateSettings() {
  const [settings, setSettings] = useState({
    markup_pct: "",
    manual_rates_enabled: false,
    manual_cad_to_ngn: "",
    manual_ngn_to_cad: "",
  });
  const [livePreview, setLivePreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: r }] = await Promise.all([
        adminApi.get("/admin/settings"),
        adminApi.get("/rates"),
      ]);
      setSettings({
        markup_pct: String(s.markup_pct ?? ""),
        manual_rates_enabled: !!s.manual_rates_enabled,
        manual_cad_to_ngn: s.manual_cad_to_ngn ? String(s.manual_cad_to_ngn) : "",
        manual_ngn_to_cad: s.manual_ngn_to_cad ? String(s.manual_ngn_to_cad) : "",
      });
      setLivePreview(r);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (overrides = {}) => {
    const merged = { ...settings, ...overrides };
    const m = parseFloat(merged.markup_pct);
    if (Number.isNaN(m) || m < 0 || m > 50) {
      toast.error("Margin must be a number between 0 and 50");
      return;
    }
    const body = { markup_pct: m, manual_rates_enabled: !!merged.manual_rates_enabled };
    if (merged.manual_rates_enabled) {
      const c2n = parseFloat(merged.manual_cad_to_ngn);
      const n2c = parseFloat(merged.manual_ngn_to_cad);
      if (Number.isNaN(c2n) || c2n <= 0 || Number.isNaN(n2c) || n2c <= 0) {
        toast.error("Manual rates must be positive numbers");
        return;
      }
      body.manual_cad_to_ngn = c2n;
      body.manual_ngn_to_cad = n2c;
    } else if (merged.manual_cad_to_ngn || merged.manual_ngn_to_cad) {
      // still persist the inputs so they're remembered when toggled back
      if (merged.manual_cad_to_ngn) body.manual_cad_to_ngn = parseFloat(merged.manual_cad_to_ngn) || 0;
      if (merged.manual_ngn_to_cad) body.manual_ngn_to_cad = parseFloat(merged.manual_ngn_to_cad) || 0;
    }
    setBusy(true);
    try {
      await adminApi.put("/admin/settings", body);
      toast.success("Settings saved");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleManual = async (val) => {
    setSettings((s) => ({ ...s, manual_rates_enabled: val }));
    // Only auto-save the toggle if user already configured rates or is disabling
    if (!val || (settings.manual_cad_to_ngn && settings.manual_ngn_to_cad)) {
      await save({ manual_rates_enabled: val });
    }
  };

  return (
    <div className="space-y-6" data-testid="rate-settings-section">
      {/* Mode toggle */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="font-display text-lg font-semibold text-[#0B192C]">Rate mode</h3>
            <p className="text-sm text-slate-500 mt-1">
              {settings.manual_rates_enabled
                ? "Manual mode active — the public calculator uses the rates you set below."
                : "Live mode active — rates fetched from market feed + margin."}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-sm font-medium inline-flex items-center gap-1 ${!settings.manual_rates_enabled ? "text-[#FF6B00]" : "text-slate-400"}`}>
              <Zap size={14} /> Live
            </span>
            <Switch
              checked={settings.manual_rates_enabled}
              onCheckedChange={toggleManual}
              disabled={loading}
              data-testid="manual-rates-toggle"
            />
            <span className={`text-sm font-medium inline-flex items-center gap-1 ${settings.manual_rates_enabled ? "text-[#FF6B00]" : "text-slate-400"}`}>
              <Hand size={14} /> Manual
            </span>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Live mode controls */}
        <div className={`bg-white rounded-2xl border p-6 ${!settings.manual_rates_enabled ? "border-[#FF6B00]/40 ring-1 ring-[#FF6B00]/10" : "border-slate-200 opacity-70"}`}>
          <h3 className="font-display text-lg font-semibold text-[#0B192C] inline-flex items-center gap-2"><Zap size={18} className="text-[#FF6B00]" /> Live rate margin</h3>
          <p className="text-sm text-slate-500 mt-1">Markup applied on top of the mid-market NGN↔CAD rate.</p>
          <div className="mt-5">
            <Label htmlFor="margin">Markup percentage</Label>
            <div className="relative mt-1.5">
              <Input
                id="margin"
                type="number"
                step="0.1"
                min="0"
                max="50"
                value={settings.markup_pct}
                onChange={(e) => setSettings({ ...settings, markup_pct: e.target.value })}
                disabled={loading || settings.manual_rates_enabled}
                data-testid="margin-input"
                className="h-11 rounded-xl pr-10"
              />
              <Percent size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>
          </div>
        </div>

        {/* Manual rates controls */}
        <div className={`bg-white rounded-2xl border p-6 ${settings.manual_rates_enabled ? "border-[#FF6B00]/40 ring-1 ring-[#FF6B00]/10" : "border-slate-200 opacity-70"}`}>
          <h3 className="font-display text-lg font-semibold text-[#0B192C] inline-flex items-center gap-2"><Hand size={18} className="text-[#FF6B00]" /> Manual rates</h3>
          <p className="text-sm text-slate-500 mt-1">Override the live feed with your own rates.</p>
          <div className="mt-5 space-y-4">
            <div>
              <Label>1 CAD = ? NGN</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={settings.manual_cad_to_ngn}
                onChange={(e) => setSettings({ ...settings, manual_cad_to_ngn: e.target.value })}
                disabled={loading || !settings.manual_rates_enabled}
                data-testid="manual-cad-to-ngn-input"
                placeholder="e.g. 985"
                className="h-11 rounded-xl mt-1.5"
              />
            </div>
            <div>
              <Label>1 NGN = ? CAD</Label>
              <Input
                type="number"
                step="0.00000001"
                min="0"
                value={settings.manual_ngn_to_cad}
                onChange={(e) => setSettings({ ...settings, manual_ngn_to_cad: e.target.value })}
                disabled={loading || !settings.manual_rates_enabled}
                data-testid="manual-ngn-to-cad-input"
                placeholder="e.g. 0.00098"
                className="h-11 rounded-xl mt-1.5"
              />
            </div>
          </div>
        </div>
      </div>

      <Button onClick={() => save()} disabled={busy || loading} data-testid="settings-save-button" className="rounded-full bg-[#FF6B00] hover:bg-[#E65C00] h-11 px-6 font-semibold">
        {busy ? "Saving…" : "Save settings"}
      </Button>

      {/* Preview */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-[#0B192C]">Live preview (what customers see)</h3>
          <button onClick={load} className="p-2 text-slate-500 hover:text-[#FF6B00]" data-testid="rate-refresh-button" aria-label="Refresh">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
        {livePreview ? (
          <dl className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div className="bg-slate-50 rounded-xl p-3">
              <dt className="text-xs text-slate-500">Source</dt>
              <dd className="font-display font-bold text-[#0B192C]" data-testid="source-display">{livePreview.source}</dd>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
              <dt className="text-xs text-slate-500">Customer CAD→NGN</dt>
              <dd className="font-display font-bold text-[#0B192C]" data-testid="customer-cad-to-ngn">{livePreview.cad_to_ngn_rate}</dd>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
              <dt className="text-xs text-slate-500">Customer NGN→CAD</dt>
              <dd className="font-display font-bold text-[#0B192C]" data-testid="customer-ngn-to-cad">{livePreview.ngn_to_cad_rate}</dd>
            </div>
            <div className="bg-orange-50 rounded-xl p-3">
              <dt className="text-xs text-[#E65C00]">Margin applied</dt>
              <dd className="font-display font-bold text-[#0B192C]" data-testid="active-margin-display">{livePreview.markup_pct}%</dd>
            </div>
          </dl>
        ) : (
          <p className="text-sm text-slate-500 mt-4">Loading…</p>
        )}
        <p className="text-xs text-slate-400 mt-4">Fetched {livePreview ? new Date(livePreview.fetched_at).toLocaleString() : "—"}</p>
      </div>
    </div>
  );
}
